import type { FeedbackClientContext, FeedbackSurface } from '@ruta/shared';

/**
 * Capture browser/page context at feedback submit time (RUT-2602).
 */
export function captureWebFeedbackContext(
  surface: FeedbackSurface,
  pagePath: string,
  pageTitle?: string,
): FeedbackClientContext {
  return {
    surface,
    pagePath,
    pageTitle: pageTitle || (typeof document !== 'undefined' ? document.title : undefined),
    appVersion: import.meta.env.VITE_APP_VERSION || undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    platform: 'web',
    locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
    timezone:
      typeof Intl !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined,
    screenWidth:
      typeof window !== 'undefined' ? window.screen?.width : undefined,
    screenHeight:
      typeof window !== 'undefined' ? window.screen?.height : undefined,
    submittedAt: new Date().toISOString(),
  };
}

/** Read a File/Blob as raw base64 (no data: prefix). */
export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function contentTypeForFile(file: File): 'image/png' | 'image/jpeg' | 'image/webp' | null {
  if (file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/webp') {
    return file.type;
  }
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.webp')) return 'image/webp';
  return null;
}
